import { LayerInfo, ColumnMapping } from "src/common/managers/Types";
import { MitStorage } from "src/managers/MitStorage";
import { SheetAnalysisResult } from "src/common/managers/Types";
import { SettingsManager } from "@viamap/viamap2-common";

export class RememberImportsManager {
   private static settingsPrefix = "lastUsedImportSettings-";
   private static stylingPrefix = "lastUsedStyling-";

   private static get maxRememberedImports(): number {
      return SettingsManager.getSystemSetting("MaxRememberedImportGroups", 50);
   }

   private static keyFromFileName(prefix: string, fileName: string): string {
      return prefix + fileName;
   }
   private static keyFromColumns(prefix: string, columns: string[]): string {
      return prefix + columns.sort().join(",");
   }

   // ---------------------------------- Import Settings ----------------------------------

   static rememberImportSettings(layerInfo: LayerInfo, checked: boolean = false) {
      if (layerInfo && layerInfo.filename) {
         const importSettings = {
            columnMapping: layerInfo.columnMapping,
            type: layerInfo.type,
            sourcePrompt: "filename", // Default to filename-based prompt
            timestamp: Date.now(),
            importTransformation: layerInfo.importTransformation,
            rememberImport: checked
         };
         MitStorage.setValue(RememberImportsManager.keyFromFileName(RememberImportsManager.settingsPrefix, layerInfo.filename), importSettings);
         if (layerInfo.analysisResult && layerInfo.analysisResult.columnNames) {
            const columnBasedSettings = {
               ...importSettings,
               rememberImport: false,
               sourcePrompt: "columns" // Use column-based prompt for column-based settings
            };
            MitStorage.setValue(RememberImportsManager.keyFromColumns(RememberImportsManager.settingsPrefix, layerInfo.analysisResult.columnNames), columnBasedSettings);
         }

         RememberImportsManager.deleteExcessRememberedImports();
      }
   }

   static clearRememberedImportSettings(layerInfo: LayerInfo) {
      if (layerInfo && layerInfo.filename) {
         MitStorage.clearValue(RememberImportsManager.keyFromFileName(RememberImportsManager.settingsPrefix, layerInfo.filename));
      }
   }

   static getRememberedImportSettings(fileName: string, sa: SheetAnalysisResult): { sourcePrompt: string, columnMapping: ColumnMapping, type?: string, importTransformation?: any, rememberImport?: boolean } | undefined {
      let result = undefined;
      let source = "none";
      if (fileName) {
         result = MitStorage.getValue(RememberImportsManager.keyFromFileName(RememberImportsManager.settingsPrefix, fileName));
         source = "filename";
      }
      if (!result && sa && sa.columnNames) {
         let key = RememberImportsManager.keyFromColumns(RememberImportsManager.settingsPrefix, sa.columnNames);
         result = MitStorage.getValue(key);
         source = "columns";
      }
      if (!result) {
         return undefined;
      }

      if (typeof result === 'object' && result && 'columnMapping' in result) {
         const storedData = result as any;
         const now = new Date();
         const createdAt = storedData.createdAt ? new Date(storedData.createdAt) : null;


         const isExpired = !createdAt || (now.getTime() - createdAt.getTime()) > (24 * 60 * 60 * 1000);

         return {
            sourcePrompt: source,
            columnMapping: storedData.columnMapping,
            type: storedData.type,
            importTransformation: storedData.importTransformation,
            rememberImport: isExpired ? false : (storedData.rememberImport ?? false)
         };
      } else {
         return { sourcePrompt: source, columnMapping: result as ColumnMapping };
      }
   }

   // ---------------------------------- Styling ----------------------------------

   static rememberStyling(layerInfo: LayerInfo) {
      if (!layerInfo) return;

      const stylingData = {
         styling: layerInfo.styling,
         propertiesToDisplay: layerInfo.propertiesToDisplay,
         timestamp: Date.now()
      };

      if (layerInfo.filename) {
         let fileKey = RememberImportsManager.keyFromFileName(RememberImportsManager.stylingPrefix, layerInfo.filename);
         MitStorage.setValue(fileKey, stylingData);
      }

      if (layerInfo.datasetname && !layerInfo.filename) {
         let datasetKey = RememberImportsManager.keyFromFileName(RememberImportsManager.stylingPrefix, layerInfo.datasetname);
         MitStorage.setValue(datasetKey, stylingData);
      }

      if (layerInfo.analysisResult && layerInfo.analysisResult.columnNames) {
         let columnsKey = RememberImportsManager.keyFromColumns(RememberImportsManager.stylingPrefix, layerInfo.analysisResult.columnNames);
         MitStorage.setValue(columnsKey, stylingData);
      }

      RememberImportsManager.deleteExcessRememberedImports();
   }

   static clearRememberedSettings(filename: string, columnNames: string[]) {
      let fileKey = RememberImportsManager.keyFromFileName(RememberImportsManager.stylingPrefix, filename);
      MitStorage.clearValue(fileKey);

      let columnsKey = RememberImportsManager.keyFromColumns(RememberImportsManager.stylingPrefix, columnNames);
      MitStorage.clearValue(columnsKey);
   }

   static getRememberedStyling(layerInfo: LayerInfo): { styling: any; propertiesToDisplay: any; } | undefined {
      let result = undefined;

      if (layerInfo && layerInfo.filename) {
         result = MitStorage.getValue(RememberImportsManager.keyFromFileName(RememberImportsManager.stylingPrefix, layerInfo.filename));

         // If no styling found with full filename, try without extension
         if (!result) {
            const filenameWithoutExt = layerInfo.filename.replace(/\.[^/.]+$/, "");
            result = MitStorage.getValue(RememberImportsManager.keyFromFileName(RememberImportsManager.stylingPrefix, filenameWithoutExt));

            // If we found styling without extension, clean up the orphaned record
            if (result) {
               console.warn("Found orphaned styling record without file extension, cleaning up:", filenameWithoutExt);
               MitStorage.clearValue(RememberImportsManager.keyFromFileName(RememberImportsManager.stylingPrefix, filenameWithoutExt));
            }
         }
      }

      if (!result && layerInfo && layerInfo.datasetname) {
         result = MitStorage.getValue(RememberImportsManager.keyFromFileName(RememberImportsManager.stylingPrefix, layerInfo.datasetname));
      }

      if (!result && layerInfo && layerInfo.analysisResult && layerInfo.analysisResult.columnNames) {
         let columnsKey = RememberImportsManager.keyFromColumns(RememberImportsManager.stylingPrefix, layerInfo.analysisResult.columnNames);
         result = MitStorage.getValue(columnsKey);
      }

      return result;
   }

   static clearRememberedStyling(layerInfo: LayerInfo) {
      if (layerInfo && layerInfo.filename) {
         MitStorage.clearValue(RememberImportsManager.keyFromFileName(RememberImportsManager.stylingPrefix, layerInfo.filename));
      }
   }

   // Clean up orphaned styling records that don't have matching import settings
   static cleanupOrphanedStylingRecords() {
      const allKeys = MitStorage.getKeys();
      const stylingKeys = allKeys.filter(key => key.startsWith(RememberImportsManager.stylingPrefix));

      stylingKeys.forEach(stylingKey => {
         const baseKey = stylingKey.substring(RememberImportsManager.stylingPrefix.length);

         // Check if there's a matching import settings record
         const importSettingsKey = RememberImportsManager.settingsPrefix + baseKey;
         const hasMatchingImportSettings = MitStorage.getValue(importSettingsKey) !== undefined;

         if (!hasMatchingImportSettings) {
            console.warn("Cleaning up orphaned styling record:", stylingKey);
            MitStorage.clearValue(stylingKey);
         }
      });
   }

   // ---------------------------------- Cleanup ----------------------------------

   static cleanupOldImports() {
      RememberImportsManager.deleteExcessRememberedImports();
   }

   private static deleteExcessRememberedImports() {
      let allKeys = MitStorage.getKeys().filter(k =>
         k.startsWith(RememberImportsManager.settingsPrefix) ||
         k.startsWith(RememberImportsManager.stylingPrefix)
      );


      let keyGroups = new Map<string, { settingsKey?: string, stylingKey?: string, timestamp: number }>();

      allKeys.forEach(key => {
         let baseKey: string;
         if (key.startsWith(RememberImportsManager.settingsPrefix)) {
            baseKey = key.substring(RememberImportsManager.settingsPrefix.length);
         } else {
            baseKey = key.substring(RememberImportsManager.stylingPrefix.length);
         }

         let obj = MitStorage.getValue(key) as any;
         let timestamp = obj?.timestamp || 0;

         if (!keyGroups.has(baseKey)) {
            keyGroups.set(baseKey, { timestamp });
         }

         let group = keyGroups.get(baseKey)!;
         if (key.startsWith(RememberImportsManager.settingsPrefix)) {
            group.settingsKey = key;
         } else {
            group.stylingKey = key;
         }

         if (timestamp > group.timestamp) {
            group.timestamp = timestamp;
         }
      });


      if (keyGroups.size > RememberImportsManager.maxRememberedImports) {
         let sortedGroups = Array.from(keyGroups.entries()).sort((a, b) => b[1].timestamp - a[1].timestamp);

         let groupsToDelete = sortedGroups.slice(RememberImportsManager.maxRememberedImports);

         if (groupsToDelete.length > 0) {
            groupsToDelete.forEach(([_baseKey, group]) => {
               if (group.settingsKey) {
                  MitStorage.clearValue(group.settingsKey);
               }
               if (group.stylingKey) {
                  MitStorage.clearValue(group.stylingKey);
               }
            });
         }
      }
   }

}
